import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { gmailQueuedMessage } from '../utils/gmailQueued';
import ReplyDropdown from '../common/ReplyDropdown';

/**
 * Booking-thread Reply / Forward using the SAME UI as the normal mail reply box
 * (common/ReplyDropdown: recipients, Cc/Bcc, template/language, rich editor, attachments).
 *
 * Wired to the same endpoints Daily Departures / the normal ticket use:
 *   reply   -> POST /email/reply/:gmailMessageId
 *   forward -> POST /email/forward/:gmailMessageId
 * The backend records the message back into bookingConversations so it shows in the thread.
 */

// '"Name" <a@b.com>' -> 'a@b.com'
const pickAddress = (str) => {
    if (!str) return '';
    return (String(str).match(/<([^>]+)>/)?.[1] || String(str).replace(/"/g, '')).trim();
};

const BookingReplyDropdown = ({ item, isForward = false, onCancel, onSent }) => {
    // recipient defaults: reply -> back to the row's recipient (supplier/customer); forward -> blank
    const defaultRecipient = isForward ? '' : pickAddress(item?.sentTo?.email || item?.data?.email?.to || '');
    const ourMailbox = pickAddress(item?.sentBy?.email || item?.data?.email?.from || '');

    const [emailValue, setEmailValue] = useState(defaultRecipient);
    const [cc, setCc] = useState('');
    const [bcc, setBcc] = useState('');
    const [showCc, setShowCc] = useState(false);
    const [showBcc, setShowBcc] = useState(false);
    const [templateselect, setTemplateselect] = useState('');
    const [template, setTemplate] = useState({ templateData: '' });
    const [textAreaValue, setTextAreaValue] = useState('');
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [submitData, setSubmitData] = useState('');
    const [isReplyloading, setIsReplyloading] = useState(false);

    const handleEmailChange = (e) => setEmailValue(e.target.value);
    const handleCcChange = (e) => setCc(e.target.value);
    const handleBccChange = (e) => setBcc(e.target.value);
    const handleFileChange = (e) => setUploadedFiles((prev) => [...prev, ...Array.from(e.target.files || [])]);
    const handleRemoveFile = (idx) => setUploadedFiles((prev) => prev.filter((_, i) => i !== idx));

    const createTask = async () => {
        const messageId = item?.gmailMessageId || item?.subThreadId || item?.messageId || '';
        const sender = ourMailbox;
        const receiver = pickAddress(emailValue);
        if (!messageId) { toast.error('Cannot send: this message has no email thread id yet'); return; }
        if (!sender) { toast.error('Sender mailbox missing'); return; }
        if (!receiver) { toast.error('Recipient email is required'); return; }
        if (!textAreaValue || !textAreaValue.trim()) { toast.error('Message is required'); return; }

        setIsReplyloading(true);
        try {
            const srcSubject = item?.data?.email?.subject || item?.subject || '';
            const prefix = isForward ? 'Fwd' : 'Re';
            const subject = new RegExp(`^${prefix}:`, 'i').test(srcSubject) ? srcSubject : `${prefix}: ${srcSubject}`;
            const cleanHTML = textAreaValue.replace(/\r\n|\r|\n/g, '');

            const formData = new FormData();
            formData.append('sender', sender);
            formData.append('receiver', receiver);
            formData.append('messageId', messageId);
            formData.append('body', cleanHTML);
            formData.append('subject', subject);
            formData.append('cc', cc || '');
            formData.append('bcc', bcc || '');
            uploadedFiles.forEach((file) => formData.append('attachments', file));

            const base = process.env.REACT_APP_BACKEND_URL;
            const url = isForward ? `${base}/email/forward/${messageId}` : `${base}/email/reply/${messageId}`;
            const sendRes = await axios.post(url, formData, { headers: { 'Content-Type': 'multipart/form-data' } });

            const queuedMsg = gmailQueuedMessage(sendRes);
            if (queuedMsg) toast.info(queuedMsg, { autoClose: 8000 });
            else toast.success(isForward ? 'Email forwarded successfully' : 'Reply sent successfully');
            setTextAreaValue('');
            setUploadedFiles([]);
            if (onSent) onSent();
            if (onCancel) onCancel();
        } catch (err) {
            toast.error(err?.response?.data?.message || err?.response?.data?.error || 'Failed to send');
        } finally {
            setIsReplyloading(false);
        }
    };

    return (
        <ReplyDropdown
            replyDropdown={true}
            setReplyDropdown={(v) => { if (!v && onCancel) onCancel(); }}
            emailValue={emailValue}
            setEmailValue={setEmailValue}
            handleEmailChange={handleEmailChange}
            cc={cc}
            bcc={bcc}
            setCc={setCc}
            setBcc={setBcc}
            showCc={showCc}
            showBcc={showBcc}
            setShowCc={setShowCc}
            setShowBcc={setShowBcc}
            setShowBccCc={() => {}}
            handleCcChange={handleCcChange}
            handleBccChange={handleBccChange}
            isForward={isForward}
            isReply={!isForward}
            templateselect={templateselect}
            setTemplateselect={setTemplateselect}
            allTemp={[]}
            template={template}
            setTemplate={setTemplate}
            textAreaValue={textAreaValue}
            setTextAreaValue={setTextAreaValue}
            visibleReplies={[]}
            selectedMail={item}
            isReplyloading={isReplyloading}
            createTask={createTask}
            handleFileChange={handleFileChange}
            uploadedFiles={uploadedFiles}
            handleRemoveFile={handleRemoveFile}
            handleDelete={() => {}}
            mode="-"
            submitData={submitData}
            setSubmitData={setSubmitData}
            // dummy non-empty label so ReplyDropdown's "add a label before sending" guard passes;
            // setLabelAnchorEl omitted -> the label chip is hidden (bookings don't use labels)
            labelname={['booking']}
        />
    );
};

export default BookingReplyDropdown;
